/**
 * Portal level-gate resolution (shared, server-authoritative-friendly).
 *
 * A portal/teleporter can declare a `minLevel` in level data. When it does
 * not, we fall back to the *recommended* level of the destination zone — the
 * low end of that zone's `levelRange` band declared in `content/zones/*.toml`
 * (compiled to `zones.gen.ts`). This keeps the gate DATA-DRIVEN: a portal
 * inherits a sane floor from its target zone, and any portal can override it
 * explicitly without code changes.
 *
 * Mirrors the resolution pattern used by `pvpResolution.ts` / `botResolution.ts`
 * — pure functions over data, no engine/DOM dependencies, importable by both
 * the client (PredictionLoop / MapPicker) and server.
 */

import { getZoneConfig } from '../content/generated/zones.gen';

/** Minimal shape a teleporter must expose for gate resolution. */
export interface PortalGateInput {
  /** Explicit per-portal floor. Wins over the zone-band fallback when set. */
  minLevel?: number;
  /** Destination zone/map id — used to look up the recommended level band. */
  targetMapId?: string;
  /**
   * Force this portal ungated, ignoring both the explicit `minLevel` and the
   * destination zone's level band. Declared by *showcase* portals only — the
   * Biome Nexus tour pads set this so anyone can hop into a high-level biome
   * to soak in its atmosphere. Real progression portals never set it, so the
   * level gating of the live world is untouched.
   */
  bypassZoneGate?: boolean;
  /**
   * Three-tier gate (on-ramp design §3): soft-warn is the DEFAULT — an
   * under-level player inside the warn band is warned but allowed through.
   * `hardGate: true` opts a portal into the old hard block ("the fog is too
   * thick"). When unset, falls back to the destination policy in
   * `HARD_GATED_DESTINATIONS`.
   */
  hardGate?: boolean;
  /**
   * How many levels *under* the floor still get warned-through (default 2 —
   * design §3: 1-2 under = brave but informed; 3+ under = would get
   * one-shot → block).
   */
  softWarnBand?: number;
}

/**
 * Gate tier (on-ramp design §3):
 *  - 'pass'  : at/above the floor — portal just works, no message.
 *  - 'warn'  : under the floor but within the soft-warn band on a non-hard
 *              portal — portal WORKS, a soft warning toast shows.
 *  - 'block' : hard-gated portal under the floor, or too far under the band —
 *              portal blocked with an explanatory toast (never an invisible
 *              wall).
 */
export type PortalGateTier = 'pass' | 'warn' | 'block';

export interface PortalGateResult {
  /** Whether the warp proceeds ('pass' and 'warn' tiers both allow it). */
  allowed: boolean;
  /** The resolved tier — drives which toast/badge the UI shows. */
  gate: PortalGateTier;
  /**
   * The effective required level for this portal (explicit minLevel, else the
   * destination zone's recommended floor, else 1). Always a positive integer.
   */
  requiredLevel: number;
  /**
   * Where `requiredLevel` came from — useful for UI copy + tests.
   *  - 'explicit'  : portal declared its own minLevel
   *  - 'zone-band' : inherited from the target zone's levelRange[0]
   *  - 'ungated'   : no minLevel + no known zone band (floor 1, always passes)
   */
  source: 'explicit' | 'zone-band' | 'ungated';
}

/** Default warn band — tunable per-portal via `softWarnBand` (design §3). */
export const DEFAULT_SOFT_WARN_BAND = 2;

/**
 * Destination-level hard-gate policy for the Hollow Coil (on-ramp design §3):
 * only the Undercroft descent is a hard gate — narratively "the fog is too
 * thick to pass," and the Hollow Keeper fight shouldn't be stumbled into
 * under-leveled. Everything else (pig_beach→tidewend, tidewend→brackenmoor,
 * coil→lobby graduation) stays soft-warn: player agency over invisible walls.
 *
 * A portal's explicit `hardGate` boolean (level-data authored, Lane A) wins
 * over this table — the table is the code-side default until level JSONs
 * carry the flag.
 */
const HARD_GATED_DESTINATIONS: ReadonlySet<string> = new Set(['coil_undercroft']);

/** Resolve whether a portal hard-blocks under-level players (vs soft-warn). */
export function isHardGated(portal: PortalGateInput): boolean {
  if (typeof portal.hardGate === 'boolean') return portal.hardGate;
  return portal.targetMapId ? HARD_GATED_DESTINATIONS.has(portal.targetMapId) : false;
}

/**
 * Resolve the effective minimum level for a portal, independent of any player.
 * Explicit `minLevel` wins; otherwise the destination zone's recommended floor
 * (`levelRange[0]`); otherwise 1 (ungated).
 */
export function resolvePortalMinLevel(portal: PortalGateInput): {
  requiredLevel: number;
  source: PortalGateResult['source'];
} {
  // Showcase override wins over everything: a portal that opts into
  // bypassZoneGate is intentionally ungated (Biome Nexus tour pads).
  if (portal.bypassZoneGate) {
    return { requiredLevel: 1, source: 'ungated' };
  }

  if (typeof portal.minLevel === 'number' && portal.minLevel > 1) {
    return { requiredLevel: Math.floor(portal.minLevel), source: 'explicit' };
  }

  if (portal.targetMapId) {
    const zone = getZoneConfig(portal.targetMapId);
    const band = zone?.levelRange?.[0];
    if (typeof band === 'number' && band > 1) {
      return { requiredLevel: Math.floor(band), source: 'zone-band' };
    }
  }

  return { requiredLevel: 1, source: 'ungated' };
}

/**
 * Evaluate whether `playerLevel` may pass through `portal` — three-tier
 * resolution (on-ramp design §3):
 *
 *  - at/above the floor → 'pass' (portal just works, no message)
 *  - under the floor, within `softWarnBand` (default 2), portal NOT hard-gated
 *    → 'warn' (portal works + soft warning toast — player agency)
 *  - under the floor on a hard-gated portal, or below the warn band
 *    → 'block' (portal halted + explanatory toast)
 *
 * When the player's level is unknown (undefined / NaN), we conservatively
 * treat them as level 1 so a fresh/un-synced client can't slip into a
 * high-level dungeon.
 *
 * A `peaceful` GM Tourist (/biomes) overrides ANY teleporter level requirement
 * (not just the showcase pads' `bypassZoneGate`) — the synced `player.peaceful`
 * flag lets a tourist freely use any teleporter they encounter on the tour.
 * `requiredLevel`/`source` still report the portal's real gate for UI copy.
 */
export function evaluatePortalGate(
  portal: PortalGateInput,
  playerLevel: number | undefined | null,
  peaceful: boolean = false,
): PortalGateResult {
  const { requiredLevel, source } = resolvePortalMinLevel(portal);
  if (peaceful) {
    return { allowed: true, gate: 'pass', requiredLevel, source };
  }
  const level = typeof playerLevel === 'number' && Number.isFinite(playerLevel)
    ? playerLevel
    : 1;

  if (level >= requiredLevel) {
    return { allowed: true, gate: 'pass', requiredLevel, source };
  }

  const band = typeof portal.softWarnBand === 'number' && portal.softWarnBand >= 0
    ? portal.softWarnBand
    : DEFAULT_SOFT_WARN_BAND;
  const withinWarnBand = level >= requiredLevel - band;

  if (!isHardGated(portal) && withinWarnBand) {
    return { allowed: true, gate: 'warn', requiredLevel, source };
  }
  return { allowed: false, gate: 'block', requiredLevel, source };
}

/** The gate fields an authored level-JSON teleporter platform may carry. */
interface PortalGatePlatform {
  minLevel?: number;
  bypassZoneGate?: boolean;
  hardGate?: boolean;
  softWarnBand?: number;
  teleporterConfig?: (Partial<Pick<PortalGateInput, 'minLevel' | 'bypassZoneGate' | 'hardGate' | 'softWarnBand'>> & object) | null;
  properties?: { teleporterLink?: { minLevel?: number } } | null;
  teleporterLink?: { minLevel?: number } | null;
}

/**
 * The gate a level-JSON teleporter platform declares, read with the same
 * precedence the client's TeleporterManager uses (nested `teleporterConfig`,
 * then the editor link, then the flat platform field). The server's portal
 * authority (world-transfer/PortalTransferPolicy) evaluates it, so a client
 * that skips its own check is still refused (lane BG, plan 27 D47).
 */
export function portalGateInputFromPlatform(platform: PortalGatePlatform, targetMapId: string): PortalGateInput {
  const config = platform.teleporterConfig ?? {};
  const editorLink = platform.properties?.teleporterLink ?? platform.teleporterLink ?? undefined;
  return {
    targetMapId,
    minLevel: config.minLevel ?? editorLink?.minLevel ?? platform.minLevel,
    bypassZoneGate: config.bypassZoneGate ?? platform.bypassZoneGate,
    hardGate: config.hardGate ?? platform.hardGate,
    softWarnBand: config.softWarnBand ?? platform.softWarnBand,
  };
}
